import { decode } from '@mapbox/polyline';
import { withStyles } from '@mui/styles';
import { Map as LMapProps } from 'leaflet';
import React, { useCallback, useEffect, useState } from 'react';
import { Polyline, Popup } from 'react-leaflet';
import { TimeAwareEncoder } from 'time-aware-polyline';

import { getColorByIndex } from '../Colors';
import { Action } from './Map';

const timeAwarePolyline = new TimeAwareEncoder();

const styles = {
  polyline: {
    strokeWidth: 10,
    'box-shadow': `inset 0 1px 2px rgba(0, 0, 0, 0.25)`,
  },
};

type RouteProps = {
  classes: {
    polyline: string;
  };
  id?: string;
  map?: LMapProps;
  path?:
    | string
    | {
        lat: number;
        lng: number;
      }[];
  timeAwarePath?:
    | string
    | {
        lat: number;
        lng: number;
      }[];
  color?: string | number;
  selected?: boolean;
  label?: string | React.ReactElement;
  zoomLevel?: number;
  showIfSelected?: boolean;
  onClick?: (...args: any[]) => any;
  onDoubleClick?: (...args: any[]) => any;
  dispatch?: React.Dispatch<Action>;
};

const Route = ({
  classes,
  id,
  map,
  children,
  color,
  dispatch,
  selected,
  label,
  zoomLevel,
  showIfSelected,
  onClick,
  onDoubleClick,
  path,
  timeAwarePath,
}: React.PropsWithChildren<RouteProps>) => {
  const childArray = React.Children.toArray(children);
  const [polyline, setPolyline] = useState<{ lat: number; lng: number }[] | undefined>(undefined);
  const [routeColor, setRouteColor] = useState<string | undefined>(
    typeof color === `string` ? color : getColorByIndex(color ?? 0)
  );
  const [routeZoom, setRouteZoom] = useState(zoomLevel);
  const [routeSelected, setRouteSelected] = useState<boolean>(selected ?? false);
  const [routeShowIfSelected, setRouteShowIfSelected] = useState<boolean>(showIfSelected ?? false);

  const parserProps = useCallback(() => {
    if (path) {
      setPolyline(
        typeof path === `string`
          ? decode(path).map((pathPoint: number[]) => ({ lat: pathPoint[0], lng: pathPoint[1] }))
          : path
      );
    } else if (timeAwarePath) {
      setPolyline(
        typeof timeAwarePath === `string`
          ? timeAwarePolyline
              .decodeTimeAwarePolyline(timeAwarePath)
              .map((timeAwarePathPoint: number[]) => ({ lat: timeAwarePathPoint[0], lng: timeAwarePathPoint[1] }))
          : timeAwarePath
      );
    }
  }, [path, timeAwarePath]);

  const setBounds = useCallback(() => {
    if (polyline === undefined) {
      return;
    }
    let [minLat, minLng, maxLat, maxLng]: number[] | undefined[] = [undefined, undefined, undefined, undefined];

    polyline.forEach((c: any) => {
      minLat = Math.min(minLat || c.lat, c.lat);
      minLng = Math.min(minLng || c.lng, c.lng);
      maxLat = Math.max(maxLat || c.lat, c.lat);
      maxLng = Math.max(maxLng || c.lng, c.lng);
    });

    if (dispatch && minLat && minLng && maxLat && maxLng) {
      dispatch({
        type: `ADD_BOUNDS`,
        bounds: [
          [minLat, minLng],
          [maxLat, maxLng],
        ],
        ...(id ? { id } : {}),
      });
    }
  }, [polyline, dispatch, id]);

  useEffect(() => {
    setBounds();
  }, [polyline, setBounds]);

  useEffect(() => {
    parserProps();
  }, [parserProps, path, timeAwarePath]);

  useEffect(() => {
    if (showIfSelected !== undefined && showIfSelected !== routeShowIfSelected) {
      setRouteShowIfSelected(showIfSelected);
    }
  }, [routeSelected, routeShowIfSelected, routeZoom, showIfSelected]);

  useEffect(() => {
    if (color !== undefined) {
      const newColor = typeof color === `number` ? getColorByIndex(color) : color;
      if (newColor !== routeColor) {
        setRouteColor(newColor);
      }
    }
  }, [color, routeColor, routeZoom, routeSelected, routeShowIfSelected]);

  useEffect(() => {
    if (selected !== undefined && selected !== routeSelected) {
      setRouteSelected(selected);
    }
  }, [color, routeSelected, routeShowIfSelected, selected, zoomLevel]);

  useEffect(() => {
    if (zoomLevel !== undefined && zoomLevel !== routeZoom) {
      setRouteZoom(zoomLevel);
    }
  }, [routeShowIfSelected, routeZoom, selected, zoomLevel]);

  const handleOnClick = (event: any) => {
    if (onClick) onClick(event);
  };

  const handleOnDoubleClick = (event: any) => {
    if (onDoubleClick) onDoubleClick(event);
  };

  return (
    <>
      {polyline && (!routeShowIfSelected || routeSelected) && (
        <>
          {/* This polyline is transparent to make the user click easier. */}
          <Polyline
            key={`route-polyline-transparent-${id}`}
            positions={polyline}
            pathOptions={{
              opacity: 0,
              fillOpacity: 0,
            }}
            className={classes.polyline}
            eventHandlers={{
              click: handleOnClick,
              dblclick: handleOnDoubleClick,
            }}
          />
          <Polyline
            key={`${routeSelected ? `route-polyline-${id}` : `selected-polyline-${id}`}`}
            positions={polyline}
            pathOptions={{
              color: routeColor,
            }}
            className={routeSelected ? classes.polyline : ``}
            eventHandlers={{
              click: handleOnClick,
              dblclick: handleOnDoubleClick,
            }}
          >
            {label && <Popup>{label}</Popup>}
          </Polyline>
        </>
      )}
      {React.Children.map(
        (childArray || []).filter(
          // @ts-expect-error
          (ch) => ch !== undefined && ch !== null && ch !== false
        ),
        (ch: any, _idx) => {
          return React.cloneElement(ch, { dispatch, color: routeColor, zoomLevel });
        }
      )}
    </>
  );
};

export default withStyles(styles)(Route);
